import {useEffect, useState} from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBars, faTimes, faPaperclip, faSpinner } from '@fortawesome/free-solid-svg-icons';
import CustomResizable from './custom-resizable';
import AttachmentsPanel from './attachments-panel';

// pdfBuff/lineItemsBuffer/xpensesBuffer are backend-issued, but never trusted
// blindly — an unexpected scheme (javascript:, data:, plain http:) must not
// reach window.fetch or <object data>. Treated the same as "not available"
// rather than surfacing a distinct error, since it's an integrity issue with
// the response, not a user-actionable failure.
// blob: is allowed alongside https: — some callers (invoice.js) create the
// buffer locally via URL.createObjectURL, which a backend response can never
// forge (blob URLs are random, origin- and session-scoped identifiers).
const isSafeDocumentUrl = (value) =>
  typeof value === 'string' && (value.startsWith('https://') || value.startsWith('blob:'));

/**
 * Shows a document's PDF in a resizable panel with a tab bar on top, shared
 * across the invoice, credit-card and expense/statement viewers.
 *
 * Only the props added for XPEN-3004 are documented below; the rest of this
 * long-lived, shared component's props predate this JSDoc block.
 *
 * @param {object} props
 * @param {"invoice"|"cc_receipt"|"expense"} [props.ownerType] - Attachment owner type (XPEN-3004). Omit entirely on screens not yet wired for attachments — no paperclip renders without both this and ownerId.
 * @param {string} [props.ownerId] - The owning record's id (invoice _id, cc statement_id, or expense account_id).
 * @param {string} [props.ownerSubId] - Expense line or transaction checksum, when applicable.
 * @param {number} [props.attachmentsCount] - Seed count shown on the paperclip's count pill; hidden at 0. `AttachmentsPanel` reports the true count back after every load and that live value takes over until this prop next changes (e.g. the caller re-fetches and passes a new value, or the owner changes) — at which point the count pill reverts to this seed until the panel reloads again.
 * @param {boolean} [props.showInvoiceExports] - (XPEN-3006) Whether the hamburger menu shows "Download XML"/"Download JSON", and whether "Download PDF" is allowed to fall back to `downloadFile(id, "pdf")` when the direct fetch of `pdfBuff` fails. All three call the invoice-only `/invoices/<id>/<format>` route — pass `false` for any caller whose `id` is not an invoice id (that route 404s/422s otherwise). When `false`, a failed PDF download shows an inline error instead of silently retrying against a route that cannot succeed.
 * @param {string} [props.primaryTabLabel] - (XPEN-3006) Label for the first tab (`pdfView === "invoice"`). Defaults to the translated word "Invoice" — override for callers whose primary document isn't an invoice (e.g. an expense receipt).
 * @param {string} [props.filenamePrefix] - (XPEN-3006) Prefix used to build the downloaded PDF's filename (`<prefix>-<id>.pdf`). Defaults to "invoice" — override to match `primaryTabLabel` for non-invoice callers.
 * @param {boolean} [props.isLoading] - (XPEN-3006) Whether the caller is still fetching pdfBuff. While true and pdfBuff is null, shows a loading spinner instead of the "PDF not available" message — a caller that clears pdfBuff to null while refetching (e.g. switching documents) would otherwise show a false "not available" on every load.
 *
 * A caller that supplies ownerType/ownerId must also supply a real
 * changePdfView — it defaults to a no-op, so the paperclip would render but
 * clicking it would silently do nothing.
 */
const PDFVisualizer = ({pdfBuff, data, downloadFile, confirmDelete, id, pdfView = "invoice", lineItemsBuffer, xpensesBuffer, changePdfView=()=>{}, redactedAccess=false, xpensesView=false, maxWidth=null, minWidth=null, ownerType=null, ownerId=null, ownerSubId=null, attachmentsCount=0, attachmentGroupLabels=null, showInvoiceExports=true, primaryTabLabel=null, filenamePrefix="invoice", isLoading=false}) => {
  // The paperclip only renders when the caller has actually wired up owner
  // info. PDFVisualizer is shared across several screens (invoice.js,
  // credit-card-transactions.jsx, review-transaction/index.jsx, expense.js) —
  // a screen that hasn't been wired yet must not show a button that cannot work.
  const isAttachmentsWired = Boolean(ownerType && ownerId);
  const [menuVisible, setMenuVisible] = useState(false);
  const [isDownloadingPdf, setIsDownloadingPdf] = useState(false);
  const [downloadFailed, setDownloadFailed] = useState(false);
  // Seeded from the caller's best guess, then self-corrected by
  // AttachmentsPanel's onAttachmentsChanged once it actually loads — so the
  // count pill never goes stale after an in-panel upload/delete without
  // requiring the caller to re-fetch and pass a fresh attachmentsCount.
  const [liveAttachmentsCount, setLiveAttachmentsCount] = useState(attachmentsCount);

  useEffect(() => {
    setLiveAttachmentsCount(attachmentsCount);
  }, [attachmentsCount, ownerType, ownerId, ownerSubId]);

  // A stale "Could not download the PDF" must not survive a document switch.
  // Watching the buffers too, not just id/pdfView, guards any caller that
  // could change what's being viewed without also changing id.
  useEffect(() => {
    setDownloadFailed(false);
  }, [id, pdfView, pdfBuff, lineItemsBuffer, xpensesBuffer]);

  const safePdfBuff = isSafeDocumentUrl(pdfBuff) ? pdfBuff : null;
  const safeLineItemsBuffer = isSafeDocumentUrl(lineItemsBuffer) ? lineItemsBuffer : null;
  const safeXpensesBuffer = isSafeDocumentUrl(xpensesBuffer) ? xpensesBuffer : null;

  const getCurrentPdfBuffer = () => {
    if (pdfView === "lineItems") return safeLineItemsBuffer;
    if (pdfView === "xpenses") return safeXpensesBuffer;
    return safePdfBuff;
  };

  const getCurrentPdfFilename = () => {
    if (pdfView === "lineItems") return `${filenamePrefix}-${id}-redacted.pdf`;
    if (pdfView === "xpenses") return `${filenamePrefix}-${id}-expenses.pdf`;
    return `${filenamePrefix}-${id}.pdf`;
  };

  // showInvoiceExports doubles as "id resolves against /invoices/<id>/<format>" —
  // when false, that route can never succeed, so falling back to it here would
  // just fail again, silently. Report the failure instead of retrying it.
  const fallbackToApiDownloadOrReportFailure = () => {
    if (showInvoiceExports) {
      downloadFile(id, "pdf");
      return;
    }
    setDownloadFailed(true);
  };

  const downloadCurrentPdf = async () => {
    const currentPdfBuffer = getCurrentPdfBuffer();
    setDownloadFailed(false);

    if (!currentPdfBuffer) {
      fallbackToApiDownloadOrReportFailure();
      return;
    }

    setIsDownloadingPdf(true);

    try {
      // Raw window.fetch, not api-wrapper, is deliberate: currentPdfBuffer is a
      // backend-issued signed storage URL, not an XPENS API path — routing it
      // through api-wrapper would attach this app's JWT to a third-party host.
      const response = await window.fetch(currentPdfBuffer);
      // window.fetch does not reject on a non-2xx status — an expired signed
      // URL returns an XML error body that .blob() would happily accept and
      // save as if it were the PDF.
      if (!response.ok) {
        throw new Error(`PDF fetch failed with status ${response.status}`);
      }
      const blob = await response.blob();
      const objectUrl = window.URL.createObjectURL(blob);

      const anchor = document.createElement("a");
      anchor.href = objectUrl;
      anchor.download = getCurrentPdfFilename();
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      window.URL.revokeObjectURL(objectUrl);
    } catch (error) {
      // Never log id (XPENS-CONFIDENTIAL) or error.message — currentPdfBuffer
      // is a signed URL, and some engines echo the request URL into a fetch
      // TypeError's message.
      console.error("PDF download failed", { pdfView, errorName: error.name });
      fallbackToApiDownloadOrReportFailure();
    } finally {
      setIsDownloadingPdf(false);
    }
  };
  
  return (
    <CustomResizable type="Invoice" maxWidth={maxWidth} minWidth={minWidth}>
      <div className="image-section position-relative">
        <div className="d-flex main-tabs">
          <div>
            <button
              className="tab-title p-3 pointer btn btn-link"
              onClick={() => setMenuVisible(true)}
              aria-label="Open menu">
              <FontAwesomeIcon icon={faBars} style={{ fontSize: "20px" }} />
            </button>
          </div>
          {menuVisible && (
            <div className="left-menu-section">
              <div>
                <div className="d-flex main-tabs mb-4">
                  <button
                    className="tab-title text-secondary p-3 pointer btn btn-link"
                    onClick={() => setMenuVisible(false)}
                    aria-label="Close menu">
                    <FontAwesomeIcon className="text-secondary" icon={faTimes} style={{ fontSize: "25px" }} />
                  </button>
                </div>
                <div>
                  <ul>
                    {showInvoiceExports && (
                      <>
                        <li>
                          <button
                            className="mock-link btn btn-link"
                            onClick={() => downloadFile(id, "elite_xml")}
                            aria-label="Download XML">
                            {data.t.read("download_xml")}
                          </button>
                        </li>
                        <li>
                          <button
                            className="mock-link btn btn-link"
                            onClick={() => downloadFile(id, "json")}
                            aria-label="Download JSON">
                            {data.t.read("download_json")}
                          </button>
                        </li>
                      </>
                    )}
                    <li>
                      <button
                        className="mock-link btn btn-link"
                        disabled={isDownloadingPdf || isLoading}
                        onClick={downloadCurrentPdf}
                        aria-label="Download PDF">
                        {isDownloadingPdf ? data.t.read("download_in_progress") : data.t.read("download_pdf")}
                      </button>
                    </li>
                    {confirmDelete &&<li>
                      <button
                        className="mock-link text-danger btn btn-link"
                        onClick={() => confirmDelete()}
                        aria-label="Delete invoice">
                        {data.t.read("delete")}
                      </button>
                    </li>}
                  </ul>
                </div>
              </div>
            </div>
          )}
          <div className={"content-section d-flex main-tabs"}>
            <div className={`flex-grow-1 p-3 tab-title  ${pdfView === "invoice" ? " tab-selected" : ""}`} onClick={()=>changePdfView("invoice")}>
              {primaryTabLabel ?? data.t.read("invoice_tab_label")}
            </div>
            {
              redactedAccess === true && 
                <div className={`flex-grow-1 p-3 tab-title  ${pdfView === "lineItems" ? " tab-selected" : ""}`} onClick={()=>changePdfView("lineItems")}>
                  {"Redacted"}
                </div>
            }
              {
                  xpensesView === true &&
                  <div className={`flex-grow-1 p-3 tab-title  ${pdfView === "xpenses" ? " tab-selected" : ""}`} onClick={()=>changePdfView("xpenses")}>
                      {data.t.read("expenses")}
                  </div>
              }

          </div>
          {isAttachmentsWired && (
            <div>
              {/* Mirrors the hamburger button, not the tabs — no
                  .tab-selected class, since that rule only applies inside
                  .content-section. aria-pressed carries the active state
                  for screen readers instead, and bg-light gives it the same
                  "currently selected" visual cue as .tab-selected. */}
              <button
                type="button"
                className={`tab-title p-3 pointer btn btn-link${pdfView === "attachments" ? " bg-light" : ""}`}
                onClick={() => changePdfView("attachments")}
                aria-pressed={pdfView === "attachments"}
                aria-label={
                  liveAttachmentsCount
                    ? `${data.t.read("attachments_tab_label")} (${liveAttachmentsCount})`
                    : data.t.read("attachments_tab_label")
                }>
                <FontAwesomeIcon icon={faPaperclip} style={{ fontSize: "20px" }} />
                {liveAttachmentsCount > 0 && (
                  // A plain sibling, not an absolutely-positioned overlay (MUI's
                  // Badge, used previously) — that overlay anchored to the
                  // icon's exact corner and offset itself out by half its own
                  // size, which rendered outside the tab bar's own bounds.
                  // Sitting next to the icon in normal flow can't misalign
                  // like that, regardless of icon size, font, or library
                  // version. Dark text on the brand orange, not white: white-
                  // on-#F39800 sits at ~2.3:1 contrast, below the 4.5:1 WCAG AA
                  // minimum for text this small; #212529 clears 9:1.
                  <span className="attachments-count-pill">{liveAttachmentsCount}</span>
                )}
              </button>
            </div>
          )}
        </div>
        {downloadFailed && (
          // Deliberately outside the hamburger menu — downloadCurrentPdf is also
          // wired to the <object> fallback content's own "Download PDF" button
          // below, which a failure can reach whether or not the menu is open.
          <div className="text-danger small p-2" role="alert">
            {data.t.read("error_download_pdf_failed")}
          </div>
        )}
        {pdfView === "attachments" && isAttachmentsWired && (
          <div className="d-flex flex-column flex-grow-1 overflow-auto">
            <AttachmentsPanel
              data={data}
              ownerType={ownerType}
              ownerId={ownerId}
              ownerSubId={ownerSubId}
              attachmentGroupLabels={attachmentGroupLabels}
              onAttachmentsChanged={setLiveAttachmentsCount}
            />
          </div>
        )}
        {pdfView === "invoice" &&
            (safePdfBuff ? (
            <object
                width="100%"
                height="100%"
                type="application/pdf"
                data={safePdfBuff}
                aria-label="PDF document">
          <div>
            {data.t.read("pdf_unsupported_browser_message")}
            <button
                className="btn btn-link"
                disabled={isDownloadingPdf || isLoading}
                onClick={downloadCurrentPdf}
                aria-label="Download PDF">
              {isDownloadingPdf ? data.t.read("download_in_progress") : data.t.read("download_pdf")}
            </button>
          </div>
            </object>
            ) : isLoading ? (
            <div className="d-flex align-items-center justify-content-center p-4 text-muted" style={{ minHeight: '200px' }} role="status" aria-live="polite">
              <FontAwesomeIcon className="infinite-rotate" icon={faSpinner} aria-hidden="true" />
              <span className="sr-only">{data.t.read("please_wait")}</span>
            </div>
            ) : (
            <div className="d-flex align-items-center justify-content-center p-4 text-muted" style={{ minHeight: '200px' }} role="status">
              {data.t.read("pdf_not_available")}
            </div>
            ))}
        {pdfView === "lineItems" && redactedAccess === true &&
            <>
              <object
                  width="100%"
                  height="100%"
                  type="application/pdf"
                  data={safeLineItemsBuffer}
                  aria-label="PDF document">
                <div>
                  {data.t.read("pdf_unsupported_browser_message")}
                  <button
                      className="btn btn-link"
                      disabled={isDownloadingPdf || isLoading}
                      onClick={downloadCurrentPdf}
                      aria-label="Download PDF">
                    {isDownloadingPdf ? data.t.read("download_in_progress") : data.t.read("download_pdf")}
                  </button>
                </div>
              </object>
            </>}
          {pdfView === "xpenses" && xpensesView === true &&
              <>
                  <object
                      width="100%"
                      height="100%"
                      type="application/pdf"
                      data={safeXpensesBuffer}
                      aria-label="PDF document">
                      <div>
                        {data.t.read("pdf_unsupported_browser_message")}
                        <button
                              className="btn btn-link"
                              disabled={isDownloadingPdf || isLoading}
                              onClick={downloadCurrentPdf}
                              aria-label="Download PDF">
                              {isDownloadingPdf ? data.t.read("download_in_progress") : data.t.read("download_pdf")}
                          </button>
                      </div>
                  </object>
              </>}
      </div>
    </CustomResizable>
  );
};

export default PDFVisualizer;
